import { useTranslation } from "react-i18next";
import {
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useCaptcha } from "../api/captcha";
import { qs, type ApiError } from "../api/client";
import { useLogin, usePublicSettings } from "../api/queries";
import { nativeRuntime, type NativeRuntime } from "../platform/native";
import { handleNativeLink } from "../platform/nativeLinks";
import { Chip } from "./Chip";
import { FeedbackLine } from "./FeedbackLine";

export function LoginDialog({
  onClose,
  onRegister,
  onForgot,
  mobileAttemptId,
  allowedProviders,
  runtime: suppliedRuntime,
}: {
  onClose: () => void;
  /** Omitted where there is nowhere to send them; the link only appears when supplied. */
  onRegister?: () => void;
  /** Same contract as `onRegister`, and gated on the same relay: no mail, no reset. */
  onForgot?: () => void;
  mobileAttemptId?: string;
  allowedProviders?: readonly string[];
  runtime?: NativeRuntime;
}) {
  const { t } = useTranslation();
  const runtime = suppliedRuntime ?? nativeRuntime();
  const settings = usePublicSettings();
  const login = useLogin(runtime);
  const { guard, verifying, failed } = useCaptcha(runtime);
  const form = useForm({
    mode: "uncontrolled",
    initialValues: { username: "", password: "" },
    validate: {
      username: isNotEmpty(t("loginDialog.usernameRequired")),
      password: isNotEmpty(t("loginDialog.passwordRequired")),
    },
  });
  const providers = (settings.data?.sso_providers ?? []).filter(
    (provider) =>
      (!runtime || provider === "google") &&
      (allowedProviders === undefined || allowedProviders.includes(provider)),
  );

  // `opened` is a constant: the caller mounts this only while it is open, which is what the
  // mutation state below relies on -- a closed-but-mounted dialog would keep a stale error.
  return (
    <Modal opened onClose={onClose} title={t("loginDialog.heading")}>
      <Stack gap="md">
        {/* `guard` runs the submit, and only if the server refuses for want of a captcha does
            it solve one and run it again. No reset on success: closing unmounts the form. */}
        <form
          onSubmit={form.onSubmit(
            (values) =>
              void guard((captcha_token) =>
                login
                  .mutateAsync(captcha_token ? { ...values, captcha_token } : values)
                  .then(onClose),
              ),
          )}
        >
          <Stack gap="sm">
            <TextInput
              data-autofocus
              label={t("loginDialog.username")}
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              key={form.key("username")}
              {...form.getInputProps("username")}
            />
            <PasswordInput
              label={t("loginDialog.password")}
              name="password"
              autoComplete="current-password"
              enterKeyHint="go"
              key={form.key("password")}
              {...form.getInputProps("password")}
            />

            {/* The server answers every failure the same way -- an unknown username and a
                wrong password are indistinguishable on purpose -- so this shows what it
                said rather than trying to be more specific. Never routed through
                `form.setErrors`: attributing it to a field would undo exactly that.

                One slot, three states. The captcha's own "required" refusal never reaches the
                user: it is an instruction to this component, not a message. */}
            <FeedbackLine role={verifying ? "status" : "alert"} c={verifying ? "dimmed" : "danger"}>
              {verifying ? (
                <Group gap="xs" wrap="nowrap">
                  <Loader size={14} />
                  <Text inherit>{t("captcha.verifying")}</Text>
                </Group>
              ) : failed ? (
                t("captcha.failed")
              ) : (
                login.isError &&
                (login.error as ApiError).captcha == null &&
                (login.error as Error).message
              )}
            </FeedbackLine>

            <Group justify="flex-end" gap="xs">
              {/* Gated on the relay alone, not on the register switch beside it: an operator who
                  has closed registration still has accounts, and a reset link is the only way
                  back into one whose password is gone. */}
              {onForgot && settings.data?.mail_enabled && (
                <Chip onClick={onForgot}>{t("loginDialog.forgotPassword")}</Chip>
              )}
              {/* Only offered when the server says registration is actually reachable --
                  it reports the switch AND a configured mail relay, so this never advertises
                  a form that cannot deliver a confirmation. */}
              {onRegister && settings.data?.registration_enabled && (
                <Chip onClick={onRegister}>{t("loginDialog.createAccount")}</Chip>
              )}
              <Chip onClick={onClose}>{t("common.cancel")}</Chip>
              <Chip type="submit" variant="accent" disabled={login.isPending || verifying}>
                {login.isPending || verifying
                  ? t("loginDialog.signingIn")
                  : t("loginDialog.submit")}
              </Chip>
            </Group>
          </Stack>
        </form>

        {providers.length > 0 && (
          <Stack gap="xs">
            <Divider label={t("loginDialog.or")} labelPosition="center" />
            {/* A plain link, not a fetch: `/start` answers 307 to the provider, so the
                browser has to be the one following it. No popup, no SDK, no script. */}
            {providers.map((name) => (
              <Button
                key={name}
                component="a"
                href={`/api/auth/sso/${name}/start${qs({ mobile_attempt_id: mobileAttemptId })}`}
                variant="default"
                fullWidth
                onClick={(event) =>
                  void handleNativeLink(event, runtime, (native) => native.signIn("login", name))
                }
              >
                {t("loginDialog.continueWith", { provider: t(`sso.${name}`) })}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
